
import { CampingSite, FrontendAmenities } from '@/services/campingSite/types';

interface AmenityRendererProps {
  site: CampingSite;
}

export const AmenityRenderer = ({ site }: AmenityRendererProps) => {
  const siteAmenities = site.amenities as FrontendAmenities;
  
  const renderAmenityIndicators = () => {
    const amenities = [];
    
    // Guest capacity
    amenities.push(`
      <div class="flex items-center gap-1.5 text-xs">
        <span class="flex items-center justify-center w-5 h-5 bg-teal-100 text-teal-600 rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M22 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
        </span>
        <span>Up to ${site.maxOccupants} guests</span>
      </div>
    `);
    
    // Pets allowed status
    amenities.push(`
      <div class="flex items-center gap-1.5 text-xs">
        <span class="flex items-center justify-center w-5 h-5 bg-amber-100 text-amber-600 rounded-full">
          <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M10 5.172C10 3.782 8.423 2.679 6.5 3c-2.823.47-4.113 6.006-4 7 .08.703 1.725 1.722 3.656 1 1.261-.472 1.96-1.45 2.344-2.5"/><path d="M14.267 5.172c0-1.39 1.577-2.493 3.5-2.172 2.823.47 4.113 6.006 4 7-.08.703-1.725 1.722-3.656 1-1.261-.472-1.855-1.45-2.239-2.5"/><path d="M8 14v.5"/><path d="M16 14v.5"/><path d="M11.25 16.25h1.5L12 17l-.75-.75Z"/><path d="M4.42 11.247A13.152 13.152 0 0 0 4 14.556C4 18.728 7.582 21 12 21s8-2.272 8-6.444c0-1.061-.162-2.2-.493-3.309m-9.243-6.082A8.801 8.801 0 0 1 12 5c.78 0 1.5.108 2.161.306"/></svg>
        </span>
        <span>${siteAmenities.petsAllowed ? 'Pets allowed' : 'No pets'}</span>
      </div>
    `);
    
    // Electric hookup
    if (site.hasElectricity || siteAmenities.electric) {
      const ampText = siteAmenities.electric50Amp ? '50AMP' : 
                     siteAmenities.amperage ? `${siteAmenities.amperage}AMP` : '';
      
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-green-100 text-green-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12h14"/><path d="m12 5 7 7-7 7"/></svg>
          </span>
          <span>Electric ${ampText}</span>
        </div>
      `);
    }
    
    // Water hookup
    if (site.hasWater || siteAmenities.water) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 2v1"/><path d="M12 21v-1"/><path d="M4.2 4.2l.78.78"/><path d="M19.02 19.02l-.78-.78"/><path d="M2 12h1"/><path d="M21 12h-1"/><path d="M4.2 19.8l.78-.78"/><path d="M19.02 4.98l-.78.78"/></svg>
          </span>
          <span>Water hookup</span>
        </div>
      `);
    }
    
    // Sewage hookup
    if (siteAmenities.sewage !== undefined) {
      const sewageColor = siteAmenities.sewage ? 'gray-100 text-gray-600' : 'red-100 text-red-600';
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-${sewageColor} rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 6h18"/><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/></svg>
          </span>
          <span>${siteAmenities.sewage ? 'Sewage hookup' : 'No sewage hookup'}</span>
        </div>
      `);
    }
    
    // WiFi availability
    if (siteAmenities.wifi !== undefined) {
      const wifiColor = siteAmenities.wifi ? 'purple-100 text-purple-600' : 'red-100 text-red-600';
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-${wifiColor} rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M5 12.55a11 11 0 0 1 14.08 0"/><path d="M1.42 9a16 16 0 0 1 21.16 0"/><path d="M8.53 16.11a6 6 0 0 1 6.95 0"/><line x1="12" y1="20" x2="12.01" y2="20"/></svg>
          </span>
          <span>${siteAmenities.wifi ? 'WiFi available' : 'No WiFi'}</span>
        </div>
      `);
    }
    
    // Site type (back-in, pull-thru, standard)
    if (siteAmenities.backIn || siteAmenities.pullThru) {
      const siteType = siteAmenities.backIn ? 'Back-in' : 'Pull-thru';
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          </span>
          <span>${siteType} site</span>
        </div>
      `);
    } else {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-indigo-100 text-indigo-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/></svg>
          </span>
          <span>Standard site</span>
        </div>
      `);
    }
    
    // Surface type
    if (siteAmenities.paved) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2" ry="2"/></svg>
          </span>
          <span>Paved site</span>
        </div>
      `);
    } else if (siteAmenities.gravelSurface) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><path d="m4.93 4.93 4.24 4.24"/><path d="m14.83 9.17 4.24-4.24"/><path d="m14.83 14.83 4.24 4.24"/><path d="m9.17 14.83-4.24 4.24"/></svg>
          </span>
          <span>Gravel surface</span>
        </div>
      `);
    } else {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-yellow-100 text-yellow-800 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2h8"/><path d="M8 2v2"/><path d="M16 2v2"/><path d="M3 7h18"/><path d="M5 7v12a3 3 0 0 0 3 3h8a3 3 0 0 0 3-3V7"/></svg>
          </span>
          <span>Unpaved site</span>
        </div>
      `);
    }
    
    // RV length
    if (siteAmenities.maxRVLength) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-cyan-100 text-cyan-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M19 17h2c.6 0 1-.4 1-1v-3c0-.9-.7-1.7-1.5-1.9C18.7 10.6 16 10 16 10s-1.3-1.4-2.2-2.3c-.5-.6-1.2-.9-1.9-1L9 6c-.9 0-1.7.5-2.2 1.3L5 10"/><path d="M9 17h6"/><path d="M19 17H9a1 1 0 0 1-1-1v-3.1L5 17v3a1 1 0 0 0 1 1h2"/><circle cx="7.5" cy="17.5" r="2.5"/><circle cx="16.5" cy="17.5" r="2.5"/></svg>
          </span>
          <span>RV up to ${siteAmenities.maxRVLength}ft</span>
        </div>
      `);
    }
    
    // Shade
    if (siteAmenities.shade || siteAmenities.shadeMorning || siteAmenities.shadeAfternoon) {
      const shadeText = siteAmenities.shade ? 'Shaded site' : 
                        siteAmenities.shadeMorning && siteAmenities.shadeAfternoon ? 'Shaded (morning & afternoon)' :
                        siteAmenities.shadeMorning ? 'Shaded (morning)' : 
                        'Shaded (afternoon)';
      
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-teal-100 text-teal-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 18.86h-5.87"/><path d="m9.13 18.86.37-1.5 4-1.5 1-3.36"/><path d="M11.83 12 10 7.5 7.5 5.5"/><path d="m10.66 12.85-1.14.73"/><path d="M18.1 13a7.3 7.3 0 0 0-5.1-12"/><path d="m6.03 10.32 11.2 6.64"/><path d="M12 2v3"/><path d="M8.24 2.34 9 5.15"/><path d="M5.15 4 7 6.46"/><path d="M4 7.5l2.84.84"/></svg>
          </span>
          <span>${shadeText}</span>
        </div>
      `);
    }
    
    // Fire pit
    if (siteAmenities.firePit) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-red-100 text-red-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 12c2-2.96 0-7-1-8 0 3.038-1.773 4.741-3 6-1.226 1.26-2 3.24-2 5a6 6 0 1 0 12 0c0-1.532-1.056-3.94-2-5-1.786 3-2.791 3-4 2z"/></svg>
          </span>
          <span>Fire pit</span>
        </div>
      `);
    }
    
    // Grill
    if (siteAmenities.grill) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-yellow-100 text-yellow-700 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M8 2a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2h-8a2 2 0 0 0-2-2Z"/><path d="M14 2v4a1 1 0 0 1-1 1h-2a1 1 0 0 1-1-1V2"/><path d="M14 2h2"/><path d="M8 2H6"/><path d="M14 22v-7"/><path d="M10 22v-9"/><path d="M6 13h12"/><path d="M10 15h4"/></svg>
          </span>
          <span>Grill</span>
        </div>
      `);
    }
    
    // Picnic table
    if (siteAmenities.picnicTable) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-yellow-100 text-yellow-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 7h18"/><path d="M6 7v5a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7"/><rect x="2" y="19" width="20" height="2" rx="1" ry="1"/><path d="M6 14v3"/><path d="M18 14v3"/></svg>
          </span>
          <span>Picnic table</span>
        </div>
      `);
    }
    
    // Near lake
    if (siteAmenities.nearLake) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-blue-100 text-blue-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 6c0 2.5 4 2.5 4 5"/><path d="m8 6 4 4"/><path d="M10 6c0 2.5 4 2.5 4 5"/><path d="M14 10c0 2.5 4 2.5 4 5"/><path d="M6 11c0 2.5 4 2.5 4 5"/><path d="M6 16c0 2.5 4 2.5 4 5"/><path d="M14 15c0 2.5 4 2.5 4 5"/></svg>
          </span>
          <span>Near lake</span>
        </div>
      `);
    }
    
    // Near restroom
    if (siteAmenities.nearRestroom) {
      amenities.push(`
        <div class="flex items-center gap-1.5 text-xs">
          <span class="flex items-center justify-center w-5 h-5 bg-gray-100 text-gray-600 rounded-full">
            <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><path d="m3 10 18 0"/></svg>
          </span>
          <span>Near restroom</span>
        </div>
      `);
    }
    
    // Format the amenities into a grid layout
    if (amenities.length > 0) {
      return `
        <div class="grid grid-cols-2 gap-x-2 gap-y-2 mt-3 mb-1">
          ${amenities.join('')}
        </div>
      `;
    }
    
    return '';
  };

  return { renderAmenityIndicators };
};
